import {
  Box,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import TaskAttachmentsList from './TaskAttachmentsList'
import TaskModuleSharedBaseFields from './TaskModuleSharedBaseFields'
import type { TaskFieldContext, TaskFieldModuleProps, ValidationResult } from './modules/types'

export type DocumentPurpose = 'collection' | 'validation' | 'approval'
export type DocumentDecision = 'approved' | 'rejected'

const PURPOSE_OPTIONS: Array<{ value: DocumentPurpose; label: string }> = [
  { value: 'collection', label: 'Collection — gather documents from customer' },
  { value: 'validation', label: 'Validation — verify submitted documents' },
  { value: 'approval', label: 'Approval — approve or reject documents' },
]

export function DocumentCollectionModule({
  field,
  task,
  formData,
  onFormDataChange,
  ticketPropertiesProps,
  isReadOnly,
}: TaskFieldModuleProps) {
  const colors = useThemeColors()
  const purpose = (formData.purpose as DocumentPurpose | undefined) ?? 'collection'
  const decision = (formData.decision as DocumentDecision | undefined) ?? ''
  const showDecision = purpose === 'validation' || purpose === 'approval'

  return (
    <Box mb={3}>
      <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">
        {field.label}
      </Text>

      <TaskModuleSharedBaseFields
        field={field}
        formData={formData}
        onFormDataChange={onFormDataChange}
        ticketPropertiesProps={ticketPropertiesProps}
      />

      <FormControl mb={3} isRequired>
        <FormLabel fontSize="xs">Purpose</FormLabel>
        <Select
          size="sm"
          value={purpose}
          onChange={(e) => {
            const next = e.target.value as DocumentPurpose
            onFormDataChange('purpose', next)
            if (next === 'collection') {
              onFormDataChange('decision', null)
            }
          }}
          isDisabled={isReadOnly}
        >
          {PURPOSE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </FormControl>

      {task.id ? (
        <Box mb={3} p={3} border="1px solid" borderColor={colors.borderLight} borderRadius="md">
          <TaskAttachmentsList taskId={task.id} isDisabled={isReadOnly} />
        </Box>
      ) : (
        <Text fontSize="sm" color={colors.textSecondary} mb={3}>
          Save the task first to upload attachments.
        </Text>
      )}

      {showDecision ? (
        <FormControl mb={3} isRequired={!isReadOnly}>
          <FormLabel fontSize="xs">Decision</FormLabel>
          <RadioGroup
            value={decision}
            onChange={(value) => onFormDataChange('decision', value)}
            isDisabled={isReadOnly}
          >
            <Stack direction="row" spacing={4}>
              <Radio value="approved" size="sm">
                Approved
              </Radio>
              <Radio value="rejected" size="sm">
                Rejected
              </Radio>
            </Stack>
          </RadioGroup>
        </FormControl>
      ) : null}
    </Box>
  )
}

export function validateDocumentCollection(ctx: TaskFieldContext): ValidationResult {
  const purpose = (ctx.formData.purpose as DocumentPurpose | undefined) ?? 'collection'
  const decision = ctx.formData.decision

  if (purpose === 'validation' || purpose === 'approval') {
    if (!decision || (decision !== 'approved' && decision !== 'rejected')) {
      return { valid: false, message: 'Record an approval decision before completing this task.' }
    }
  }

  return { valid: true }
}
